import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Calendar, MapPin, TrendingUp } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { formatDateAU } from "@/lib/formatDateAU";

const STATUS_PILL = {
  clean: { bg: "bg-emerald-100", text: "text-emerald-800", label: "No significant issues identified" },
  issues: { bg: "bg-amber-100", text: "text-amber-800", label: "Issues identified — review recommendations" },
};

// Result states — keeps render logic tidy and untyped issues from collapsing
// every non-2xx response into a generic "Certificate not found".
const STATE = {
  LOADING: "loading",
  VALID: "valid",
  NOT_FOUND: "not_found",
  DISABLED: "disabled",
  ERROR: "error",
};

export default function VerifyCertificate() {
  const { certNumber } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState(STATE.LOADING);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setState(STATE.LOADING);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/conveyancing/verify/${certNumber}`,
        );

        // Read the body exactly once — even when status is non-2xx so the
        // error branch never tries to consume an already-drained stream.
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // Body wasn't JSON (or already drained by a dev overlay). Treat as
          // a generic error rather than a missing certificate.
          if (!cancelled) setState(STATE.ERROR);
          return;
        }

        if (cancelled) return;

        if (response.ok && payload?.valid) {
          setData(payload);
          setState(STATE.VALID);
        } else if (response.status === 404 || payload?.valid === false) {
          setState(STATE.NOT_FOUND);
        } else if (response.status === 403 && payload?.detail === "feature_disabled") {
          setState(STATE.DISABLED);
        } else {
          setState(STATE.ERROR);
        }
      } catch {
        if (!cancelled) setState(STATE.ERROR);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [certNumber]);

  return (
    <div data-testid="verify-cert-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <span className="text-xs text-slate-500 hidden sm:block">Certificate verification</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {state === STATE.LOADING && (
          <div data-testid="verify-loading" className="text-center text-slate-500">Verifying...</div>
        )}

        {state === STATE.NOT_FOUND && (
          <div data-testid="verify-invalid" className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Certificate not found</h1>
            <p className="text-sm text-slate-500 mt-2">
              No Conveyancing Water Certificate™ exists with number <code className="font-mono">{certNumber}</code>.
              Double-check the number and try again.
            </p>
          </div>
        )}

        {state === STATE.DISABLED && (
          <div data-testid="verify-disabled" className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-slate-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Verification temporarily unavailable</h1>
            <p className="text-sm text-slate-500 mt-2">
              Public certificate verification is currently disabled. Please contact Aware Water Testing on{" "}
              <a href="tel:+61753273473" className="font-semibold text-cyan-700 hover:text-cyan-800">(07) 5327 3473</a> for assistance.
            </p>
          </div>
        )}

        {state === STATE.ERROR && (
          <div data-testid="verify-error" className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Unable to verify certificate</h1>
            <p className="text-sm text-slate-500 mt-2">
              Something went wrong while looking up <code className="font-mono">{certNumber}</code>. Please try again in a moment, or contact us if the problem persists.
            </p>
          </div>
        )}

        {state === STATE.VALID && data && (
          <div data-testid="verify-valid" className="bg-white rounded-3xl border border-emerald-200 p-8 lg:p-10 shadow-sm">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified Authentic
            </div>
            <h1 className="font-display mt-4 text-3xl font-bold text-slate-900">Conveyancing Water Certificate™</h1>
            <div className="mt-1 font-mono text-sm text-slate-500">{data.cert_number}</div>

            <dl className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Property</dt>
                <dd className="mt-1 text-slate-900 font-medium flex items-start gap-1.5"><MapPin className="w-4 h-4 text-slate-400 mt-1" /><span>{data.property_address || "—"}</span></dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Issue date</dt>
                <dd className="mt-1 text-slate-900 font-medium flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-400" />{formatDateAU(data.generated_at)}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Water Health Score™</dt>
                <dd className="mt-1 font-display text-3xl font-bold text-slate-900">{data.snapshot?.health_score ?? "—"}<span className="text-base font-semibold text-slate-500">/100</span></dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Tests on record</dt>
                <dd className="mt-1 text-slate-900"><span className="font-display text-2xl font-bold">{data.snapshot?.test_count || 0}</span> <span className="text-sm text-slate-500 capitalize ml-2"><TrendingUp className="inline w-3.5 h-3.5 mr-0.5" />{data.snapshot?.trend || "stable"}</span></dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Water source</dt>
                <dd className="mt-1 text-slate-900 font-medium">{data.snapshot?.water_source || "—"}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Latest test</dt>
                <dd className="mt-1 text-slate-900 font-medium">{formatDateAU(data.snapshot?.latest_test_date)}</dd>
              </div>
            </dl>

            {data.snapshot?.status && STATUS_PILL[data.snapshot.status] && (
              <div className={`mt-7 rounded-2xl p-4 ${STATUS_PILL[data.snapshot.status].bg} ${STATUS_PILL[data.snapshot.status].text}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">Certificate status</div>
                <div className="mt-0.5 font-semibold">{STATUS_PILL[data.snapshot.status].label}</div>
              </div>
            )}

            {data.white_label_brand && (
              <p className="mt-5 text-xs text-slate-500">Issued under the brand <strong>{data.white_label_brand}</strong> via Aware Water Testing.</p>
            )}

            <p className="mt-6 text-[11px] text-slate-400 leading-relaxed">
              This Conveyancing Water Certificate™ has been verified against the AWARE Water Health Records held by Aware Water Testing. The information shown reflects the testing data attached to the property&apos;s Passport at the time of issue. This page does not replace independent professional advice.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
